import { X } from 'lucide-react';
import type { AuditFilters, SecurityEventType } from '@yawi/types';
import { Button, Input, Select } from '@/core/ui/components';
import { formatEventType } from './AuditLogTable';

interface AuditLogFiltersProps {
  filters: AuditFilters;
  onFiltersChange: (filters: AuditFilters) => void;
  actors: { id: string; name: string }[];
}

/** Valores de `security_audit_logs.event_type` */
const EVENT_TYPES: SecurityEventType[] = [
  'ADMIN_ACTION',
  'SUPER_ADMIN_TENANT_ACCESS',
  'ADMIN_MEMBER_IMPERSONATION',
  'LOGIN_SUCCESS',
  'LOGIN_FAILED',
  'LOGIN_BLOCKED',
  'LOGOUT',
  'ACCOUNT_LOCKED',
  'ACCOUNT_UNLOCKED',
  'PASSWORD_CHANGED',
  'PASSWORD_RESET_REQUESTED',
  'TOKEN_REFRESH',
  'TOKEN_REUSE_DETECTED',
  'TOKEN_REVOKED',
  'TOKEN_FAMILY_REVOKED',
  'API_KEY_CREATED',
  'API_KEY_REVOKED',
];

const eventTypeOptions = [
  { value: '', label: 'All Events' },
  ...EVENT_TYPES.map((type) => ({ value: type, label: formatEventType(type) })),
];

export function AuditLogFilters({ filters, onFiltersChange, actors }: AuditLogFiltersProps) {
  const actorOptions = [
    { value: '', label: 'All Actors' },
    ...actors.map((a) => ({ value: a.id, label: a.name })),
  ];

  const handleChange = (key: keyof AuditFilters, value: string) => {
    onFiltersChange({
      ...filters,
      [key]: value || undefined,
    });
  };

  const hasActiveFilters =
    filters.eventType || filters.userId || filters.dateFrom || filters.dateTo;

  return (
    <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
      <Select
        label="Event"
        value={filters.eventType || ''}
        onChange={(e) => handleChange('eventType', e.target.value)}
        options={eventTypeOptions}
      />

      <Select
        label="Actor"
        value={filters.userId || ''}
        onChange={(e) => handleChange('userId', e.target.value)}
        options={actorOptions}
      />

      <Input
        label="From"
        type="date"
        value={filters.dateFrom || ''}
        onChange={(e) => handleChange('dateFrom', e.target.value)}
      />

      <Input
        label="To"
        type="date"
        value={filters.dateTo || ''}
        onChange={(e) => handleChange('dateTo', e.target.value)}
      />

      {hasActiveFilters && (
        <Button variant="ghost" onClick={() => onFiltersChange({})} leftIcon={<X className="w-4 h-4" />}>
          Clear
        </Button>
      )}
    </div>
  );
}
